import ApiService from '../api'

class EmailService {
  /**
   * Get paginated list of email templates
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  async getEmailTemplates(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      if (params.page) queryParams.append('page', params.page)
      if (params.per_page) queryParams.append('per_page', params.per_page)
      if (params.type) queryParams.append('type', params.type)
      if (params.search) queryParams.append('search', params.search)

      return await ApiService.get(`/email-templates?${queryParams.toString()}`)
    } catch (error) {
      console.error('Get email templates error:', error)
      throw error
    }
  }

  /**
   * Get single email template by ID
   * @param {string} templateId - Template UUID
   * @returns {Promise}
   */
  async getEmailTemplate(templateId) {
    try {
      return await ApiService.get(`/email-templates/${templateId}`)
    } catch (error) {
      console.error('Get email template error:', error)
      throw error
    }
  }

  /**
   * Create new email template
   * @param {Object} templateData - Template data
   * @returns {Promise}
   */
  async createEmailTemplate(templateData) {
    try {
      return await ApiService.post('/email-templates', {}, templateData)
    } catch (error) {
      console.error('Create email template error:', error)
      throw error
    }
  }

  /**
   * Update existing email template
   * @param {string} templateId - Template UUID
   * @param {Object} templateData - Updated template data
   * @returns {Promise}
   */
  async updateEmailTemplate(templateId, templateData) {
    try {
      return await ApiService.put(`/email-templates/${templateId}`, {}, templateData)
    } catch (error) {
      console.error('Update email template error:', error)
      throw error
    }
  }

  /**
   * Soft delete email template
   * @param {string} templateId - Template UUID
   * @returns {Promise}
   */
  async deleteEmailTemplate(templateId) {
    try {
      return await ApiService.delete(`/email-templates/${templateId}`)
    } catch (error) {
      console.error('Delete email template error:', error)
      throw error
    }
  }

  /**
   * Toggle template active/inactive status
   * @param {string} templateId - Template UUID
   * @returns {Promise}
   */
  async toggleTemplateStatus(templateId) {
    try {
      return await ApiService.put(`/email-templates/${templateId}/toggle-status`, {}, {})
    } catch (error) {
      console.error('Toggle template status error:', error)
      throw error
    }
  }

  /**
   * Get available email events for automation
   * @returns {Promise}
   */
  async getAvailableEvents() {
    try {
      return await ApiService.get('/email-templates/events')
    } catch (error) {
      console.error('Get available events error:', error)
      throw error
    }
  }

  /**
   * Send email using template
   * @param {string} templateId - Template UUID
   * @param {Object} emailData - Email sending data
   * @returns {Promise}
   */
  async sendEmailWithTemplate(templateId, emailData) {
    try {
      return await ApiService.post(`/email-templates/${templateId}/send`, {}, emailData)
    } catch (error) {
      console.error('Send email error:', error)
      throw error
    }
  }

  /**
   * Preview email template with variables
   * @param {string} templateId - Template UUID
   * @param {Object} variables - Variables for preview
   * @returns {Promise}
   */
  async previewEmailTemplate(templateId, variables = {}) {
    try {
      const queryParams = new URLSearchParams()
      if (Object.keys(variables).length > 0) {
        queryParams.append('variables', JSON.stringify(variables))
      }
      return await ApiService.get(`/email-templates/${templateId}/preview?${queryParams.toString()}`)
    } catch (error) {
      console.error('Preview email template error:', error)
      throw error
    }
  }

  /**
   * Send a test email for a template
   * @param {string} templateId - Template UUID
   * @param {Object} payload - { test_email, variables }
   * @returns {Promise}
   */
  async testTemplate(templateId, payload) {
    try {
      return await ApiService.post(`/email-templates/${templateId}/test`, {}, payload)
    } catch (error) {
      console.error('Test template email error:', error)
      throw error
    }
  }

  /**
   * Send custom email (without template) - Enhanced for bulk sending
   * @param {Object} emailData - Email data
   * @returns {Promise}
   */
  async sendCustomEmail(emailData) {
    try {
      // Convert recipients array to comma-separated string if needed
      if (Array.isArray(emailData.recipients)) {
        emailData.recipients = emailData.recipients.join(', ')
      }

      // Convert CC and BCC arrays to comma-separated strings if needed
      if (Array.isArray(emailData.cc)) {
        emailData.cc = emailData.cc.join(', ')
      }

      if (Array.isArray(emailData.bcc)) {
        emailData.bcc = emailData.bcc.join(', ')
      }

      return await ApiService.post('/emails/send', {}, emailData)
    } catch (error) {
      console.error('Send custom email error:', error)
      throw error
    }
  }

  /**
   * Get email logs/audit trail
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  async getEmailLogs(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      if (params.page) queryParams.append('page', params.page)
      if (params.per_page) queryParams.append('per_page', params.per_page)
      if (params.status) queryParams.append('status', params.status)
      if (params.type) queryParams.append('type', params.type)
      if (params.recipient) queryParams.append('recipient', params.recipient)
      if (params.template_id) queryParams.append('template_id', params.template_id)
      if (params.date_from) queryParams.append('date_from', params.date_from)
      if (params.date_to) queryParams.append('date_to', params.date_to)
      if (params.search) queryParams.append('search', params.search)

      return await ApiService.get(`/email-logs?${queryParams.toString()}`)
    } catch (error) {
      console.error('Get email logs error:', error)
      throw error
    }
  }

  /**
   * Get email history with enhanced filtering
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  async getEmailHistory(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      if (params.page) queryParams.append('page', params.page)
      if (params.per_page) queryParams.append('per_page', params.per_page)
      if (params.status) queryParams.append('status', params.status)
      if (params.type) queryParams.append('type', params.type)
      if (params.recipient) queryParams.append('recipient', params.recipient)
      if (params.template_id) queryParams.append('template_id', params.template_id)
      if (params.date_from) queryParams.append('date_from', params.date_from)
      if (params.date_to) queryParams.append('date_to', params.date_to)
      if (params.search) queryParams.append('search', params.search)

      return await ApiService.get(`/email-management/history?${queryParams.toString()}`)
    } catch (error) {
      console.error('Get email history error:', error)
      throw error
    }
  }

  /**
   * Get single email log by ID
   * @param {number} logId - Log ID
   * @returns {Promise}
   */
  async getEmailLog(logId) {
    try {
      return await ApiService.get(`/email-logs/${logId}`)
    } catch (error) {
      console.error('Get email log error:', error)
      throw error
    }
  }

  /**
   * Resend failed email
   * @param {number} logId - Log ID
   * @returns {Promise}
   */
  async resendEmail(logId) {
    try {
      return await ApiService.post(`/email-logs/${logId}/resend`, {}, {})
    } catch (error) {
      console.error('Resend email error:', error)
      throw error
    }
  }

  /**
   * Get email statistics - Enhanced version
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  async getEmailStats(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      if (params.date_from) queryParams.append('date_from', params.date_from)
      if (params.date_to) queryParams.append('date_to', params.date_to)

      return await ApiService.get(`/email-templates/statistics?${queryParams.toString()}`)
    } catch (error) {
      console.error('Get email stats error:', error)
      throw error
    }
  }

  /**
   * Get detailed email statistics for dashboard
   * @param {Object} params - Query parameters
   * @returns {Promise}
   */
  async getDetailedEmailStats(params = {}) {
    try {
      const queryParams = new URLSearchParams()
      if (params.date_from) queryParams.append('date_from', params.date_from)
      if (params.date_to) queryParams.append('date_to', params.date_to)

      return await ApiService.get(`/email-management/stats?${queryParams.toString()}`)
    } catch (error) {
      console.error('Get detailed email stats error:', error)
      throw error
    }
  }

  /**
   * Test email configuration
   * @param {Object} testData - Test configuration data
   * @returns {Promise}
   */
  async testEmailConfig(testData = {}) {
    try {
      return await ApiService.post('/emails/test-config', {}, testData)
    } catch (error) {
      console.error('Test email config error:', error)
      throw error
    }
  }

  /**
   * Get mail server status (e.g., Mailhog)
   * @returns {Promise}
   */
  async getMailServerStatus() {
    try {
      return await ApiService.get('/emails/mailhog-status')
    } catch (error) {
      console.error('Get mail server status error:', error)
      throw error
    }
  }

  /**
   * Bulk operations helper methods
   */

  /**
   * Send bulk emails to multiple recipients using template
   * @param {string} templateId - Template UUID
   * @param {Array} recipients - Array of recipient objects
   * @param {Object} globalVariables - Variables to apply to all emails
   * @returns {Promise}
   */
  async sendBulkTemplateEmails(templateId, recipients, globalVariables = {}) {
    try {
      const results = []

      for (const recipient of recipients) {
        const emailData = {
          recipient: recipient.email,
          variables: {
            ...globalVariables,
            user_name: recipient.name || recipient.email,
            ...recipient.variables
          }
        }

        try {
          const result = await this.sendEmailWithTemplate(templateId, emailData)
          results.push({
            recipient: recipient.email,
            success: true,
            data: result
          })
        } catch (error) {
          results.push({
            recipient: recipient.email,
            success: false,
            error: error.response?.data?.message || error.message
          })
        }
      }

      return {
        success: true,
        data: {
          total: recipients.length,
          successful: results.filter(r => r.success).length,
          failed: results.filter(r => !r.success).length,
          results: results
        }
      }
    } catch (error) {
      console.error('Send bulk template emails error:', error)
      throw error
    }
  }

  /**
   * Validate email addresses
   * @param {Array|string} emails - Email addresses to validate
   * @returns {Object} Validation results
   */
  validateEmails(emails) {
    const emailArray = Array.isArray(emails) ? emails : emails.split(',').map(e => e.trim())
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    const valid = []
    const invalid = []

    emailArray.forEach(email => {
      if (email && emailRegex.test(email)) {
        valid.push(email)
      } else if (email) {
        invalid.push(email)
      }
    })

    return {
      valid,
      invalid,
      isValid: invalid.length === 0,
      totalValid: valid.length,
      totalInvalid: invalid.length
    }
  }

  /**
   * Parse email addresses from various formats
   * @param {string} emailsString - Comma or semicolon separated emails
   * @returns {Array} Array of cleaned email addresses
   */
  parseEmailAddresses(emailsString) {
    if (!emailsString) return []

    return emailsString
      .split(/[,;]/)
      .map(email => email.trim())
      .filter(email => email.length > 0)
  }

  /**
   * Format email recipients for display
   * @param {Array} recipients - Array of email addresses
   * @param {number} maxDisplay - Maximum number to display before truncating
   * @returns {string} Formatted string
   */
  formatRecipients(recipients, maxDisplay = 3) {
    if (!Array.isArray(recipients) || recipients.length === 0) {
      return 'No recipients'
    }

    if (recipients.length <= maxDisplay) {
      return recipients.join(', ')
    }

    const displayed = recipients.slice(0, maxDisplay).join(', ')
    const remaining = recipients.length - maxDisplay
    return `${displayed} and ${remaining} more`
  }

  /**
   * Get template variables from content
   * @param {string} content - Template content
   * @returns {Array} Array of variable names
   */
  extractTemplateVariables(content) {
    if (!content) return []

    const variableRegex = /\{\{([^}]+)\}\}/g
    const variables = []
    let match

    while ((match = variableRegex.exec(content)) !== null) {
      const variable = match[1].trim()
      if (!variables.includes(variable)) {
        variables.push(variable)
      }
    }

    return variables
  }

  /**
   * Prepare template preview data
   * @param {Object} template - Template object
   * @param {Object} variables - Variables for preview
   * @returns {Object} Preview data
   */
  preparePreviewData(template, variables = {}) {
    const subjectVariables = this.extractTemplateVariables(template.subject || '')
    const bodyVariables = this.extractTemplateVariables(template.body || '')
    const allVariables = [...new Set([...subjectVariables, ...bodyVariables])]

    // Provide default values for missing variables
    const defaultVariables = {
      user_name: 'John Doe',
      app_name: 'EDCTP Portal',
      login_url: 'https://app.example.com/login',
      support_email: 'support@example.com'
    }

    const previewVariables = { ...defaultVariables, ...variables }

    return {
      variables: allVariables,
      previewVariables,
      missingVariables: allVariables.filter(v => !previewVariables.hasOwnProperty(v))
    }
  }
}

export default new EmailService()
